import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ImageUploadField from '../../components/ImageUploadField';
import { fetchData } from '../../helpers/apiUtils';
import type { HubBroker } from './types';

/**
 * Name, Logo, Zahlungsmethoden, Status und Notizen eines KATALOG-Brokers ändern.
 *
 * ⚠ Der Slug fehlt mit Absicht und steht nur als gesperrte Anzeige da: er ist Identität —
 * Nachschlage-Schlüssel für Profile und Import, denormalisiert in den Diskrepanzen, in `page_slug`
 * eingebacken und externe Kennung im Content-Feed. Die Route weist ihn im Rumpf mit 400 ab.
 *
 * ⚠ Und der Dialog sagt ausdrücklich, WO der Wert wirkt. Der naheliegende Irrtum ist, dass hier
 * das Logo einer Broker-Erfahrungsseite hängt — das tut es nicht: eine Seite mit eigenem Broker
 * baut kein Roster auf, ihr Logo ist ein redaktionelles Feld im Dokument. Ohne diesen Satz ändert
 * jemand hier den Wert, sieht auf der Seite nichts und meldet einen Fehler, den es nicht gibt.
 */

/** Ein Eintrag aus `GET /api/broker-hub/payment-methods` (Katalog in `zahlungsmittel.ts`). */
interface Zahlungsmittel {
  key: string;
  label: string;
  icon: string;
}

const STATUS: Array<{ wert: string; label: string }> = [
  { wert: 'active', label: 'aktiv' },
  { wert: 'paused', label: 'pausiert' },
  { wert: 'retired', label: 'stillgelegt' },
];

interface Props {
  broker: HubBroker;
  onSchliessen: () => void;
  /** Nach erfolgreichem Speichern — der Aufrufer lädt die Matrix neu. */
  onGespeichert: () => void;
}

const BrokerEditDialog: React.FC<Props> = ({ broker, onSchliessen, onGespeichert }) => {
  const [name, setName] = useState(broker.name);
  const [logoUrl, setLogoUrl] = useState(broker.logo_url ?? '');
  const [logoMarkUrl, setLogoMarkUrl] = useState(broker.logo_mark_url ?? '');
  const [status, setStatus] = useState(broker.status);
  const [notes, setNotes] = useState(broker.notes ?? '');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Zahlungsmethoden: EINE Auswahl für alle Portale. `angefasst` entscheidet, ob sie mitgeschickt
  // wird — ein Speichern von Name oder Logo darf aus „nicht zentral gepflegt“ (null) kein `[]`
  // machen, sonst verlören alle Karten des Brokers ihre Icons, ohne dass jemand das wollte.
  const [katalog, setKatalog] = useState<Zahlungsmittel[] | null>(null);
  const [katalogFehler, setKatalogFehler] = useState(false);
  const [auswahl, setAuswahl] = useState<string[]>(broker.payment_methods ?? []);
  const [angefasst, setAngefasst] = useState(false);
  const zentralGepflegt = angefasst || Array.isArray(broker.payment_methods);

  useEffect(() => {
    let cancelled = false;
    fetchData<Zahlungsmittel[]>('/api/broker-hub/payment-methods').then((res) => {
      if (cancelled) return;
      if (res.success && Array.isArray(res.data)) setKatalog(res.data);
      else setKatalogFehler(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const umschalten = (key: string) => {
    setAngefasst(true);
    setAuswahl((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const speichern = async () => {
    setSpeichert(true);
    setFehler(null);
    const res = await fetchData<HubBroker>(`/api/broker-hub/brokers/${broker.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name,
        logo_url: logoUrl.trim() || null,
        // Leer -> null, nicht ''. Nur `null` loest den Rueckfall auf das breite Logo aus.
        logo_mark_url: logoMarkUrl.trim() || null,
        status,
        notes: notes.trim() || null,
        // Nur wenn angefasst — fehlt der Schlüssel, bleibt die gespeicherte Auswahl stehen.
        ...(angefasst ? { payment_methods: auswahl } : {}),
        // Optimistische Sperre: schreibt der Import (oder jemand anderes) dazwischen, kommt 409
        // statt eines stillen Überschreibens.
        expected_updated_at: broker.updated_at,
      }),
    });
    setSpeichert(false);
    if (res.success) {
      onGespeichert();
      onSchliessen();
      return;
    }
    setFehler(res.error || 'Speichern fehlgeschlagen.');
  };

  return (
    <Dialog open onClose={onSchliessen} maxWidth="sm" fullWidth>
      <DialogTitle>Broker bearbeiten — {broker.slug}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {fehler && <Alert severity="error">{fehler}</Alert>}

          <TextField
            label="Kennung (Slug)"
            value={broker.slug}
            size="small"
            disabled
            helperText="Unveränderlich — Profile, Import, Diskrepanzen und der Content-Feed schlagen den Broker darüber nach."
          />

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            required
          />

          <ImageUploadField
            label="Logo breit (Steckbrief, Überschrift)"
            uploadUrl="/api/broker-content/upload-image?kind=logos"
            value={logoUrl}
            onChange={setLogoUrl}
          />

          <ImageUploadField
            label="Logo quadratisch (Kachel in der Rangliste)"
            uploadUrl="/api/broker-content/upload-image?kind=logos"
            value={logoMarkUrl}
            onChange={setLogoMarkUrl}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Die Rangliste zeigt das quadratische Logo in einer 42px-Kachel. Ist keines hinterlegt,
            nimmt sie das breite — das wird dort schnell unleserlich.
          </Typography>

          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="small"
            helperText={
              status === 'active'
                ? undefined
                : 'Alles ausser „aktiv" sperrt den Live-Deploy jeder Vergleichsseite, die diesen Broker zeigt.'
            }
          >
            {STATUS.map((s) => (
              <MenuItem key={s.wert} value={s.wert}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="subtitle2">Zahlungsmethoden (Übersichtskarten)</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              Gilt für die Karten auf allen Portalen, die Icons kommen automatisch. Wirkt nach
              „Ausspielen“.
            </Typography>
            {katalogFehler && (
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                Die Liste der Zahlungsmethoden konnte nicht geladen werden — die Auswahl bleibt
                unverändert.
              </Alert>
            )}
            {!katalog && !katalogFehler && (
              <Typography variant="body2" color="text.secondary">
                Lädt…
              </Typography>
            )}
            {katalog && (
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(190px, 1fr))"
                data-testid="zahlungsmittel-auswahl"
              >
                {katalog.map((z) => (
                  <FormControlLabel
                    key={z.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={auswahl.includes(z.key)}
                        onChange={() => umschalten(z.key)}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={1}>
                        <img src={z.icon} alt="" style={{ height: 18, width: 'auto' }} />
                        <Typography variant="body2">{z.label}</Typography>
                      </Box>
                    }
                  />
                ))}
              </Box>
            )}
            {!zentralGepflegt && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Noch nicht zentral gepflegt — die Karten zeigen bisher ihre eigenen Icons je
                Portal. Sobald hier etwas angehakt ist, gilt diese Auswahl überall.
              </Typography>
            )}
            {zentralGepflegt && auswahl.length === 0 && (
              <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 0.5 }}>
                Nichts angehakt — die Karten zeigen dann keine Zahlungsmethoden.
              </Typography>
            )}
          </Box>

          <TextField
            label="Notizen (intern)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            multiline
            minRows={2}
          />

          <Alert severity="info" sx={{ borderRadius: 1 }}>
            <Typography variant="body2" component="div">
              <strong>Wo diese Werte wirken:</strong> in dieser Übersicht, auf{' '}
              <strong>Vergleichsseiten</strong> (dort erst nach dem nächsten Ausspielen) und im
              Content-Feed für die Portale (dort binnen einer Minute). Die{' '}
              <strong>Zahlungsmethoden</strong> auf den Übersichtskarten aller Portale, nach
              „Ausspielen“.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>Nicht</strong> auf den Erfahrungsseiten des Brokers — deren Name und Logo
              stehen als Felder im Baustein-Editor der jeweiligen Seite.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSchliessen} disabled={speichert}>
          Abbrechen
        </Button>
        <Button variant="contained" onClick={speichern} disabled={speichert || !name.trim()}>
          {speichert ? 'Speichert…' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerEditDialog;
